import {
  DynamicSystemPromptRepeater,
  ExtractedDataFieldRepeater,
  FunctionRepeater,
  MetadataFieldRepeater,
} from '@/components';
import { AuthContext } from '@/contexts';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AnswersModality,
  ChatFunction,
  Direction,
  DynamicSystemPrompt,
  ModelMetadata,
  PromptVariable,
  Role,
  UpdateForm,
} from 'types';
import { fetchWithAuth } from 'utils';
import { apiUrl, CHAT_METADATA_KEY } from 'utils/constants';

import { InfoLabelTooltipContent, InfoLabelTooltipTrigger } from 'components/InfoLabelTooltip';
import { Button } from 'components/shadcn/button';
import { Checkbox } from 'components/shadcn/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/shadcn/dialog';
import { Input } from 'components/shadcn/input';
import { Label } from 'components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { Tooltip } from 'components/shadcn/tooltip';
import { useChatbotList } from 'hooks/useChatbotList';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const DEFAULT_COST_LIMIT = 100;

const UpdateChatForm = forwardRef<HTMLFormElement>((props, ref) => {
  const queryClint = useQueryClient();
  const { t } = useTranslation();
  const [isDynamicPromptDialogOpen, setIsDynamicPromptDialogOpen] = useState(false);
  const [modelOptions, setModelOptions] = useState<ModelMetadata[]>([]);
  const { id } = useParams();
  const query = new URLSearchParams(location.search);
  const { refetch } = useChatbotList();
  const duplicateChatId = query.get('duplicateChatId');
  const navigate = useNavigate();
  const { role, canEditDynamicPrompts } = useContext(AuthContext);
  const isAdmin = role === Role.ADMIN;

  const methods = useForm({
    defaultValues: {
      costLimit: DEFAULT_COST_LIMIT,
      assistantEmail: '',
      answersModality: AnswersModality.TEXT,
      name: '',
      systemPrompt: t('dashboard.defaults.systemPrompt'),
      dynamicSystemPrompts: [],
      whatsappPrompt: '',
      firstMessage: t('dashboard.defaults.firstMessage'),
      modelId: 0,
      canAcceptVoiceMessages: false,
      direction: Direction.RTL,
      textFieldLabel: '',
      openaiKey: '',
      googleKey: '',
      twilioAccountSid: '',
      twilioAuthToken: '',
      twilioMessagingServiceSid: '',
      sendgridApiKey: '',
      airtableLogsBase: '',
      customUserText: '',
      customUserBackground: '',
      temperature: 0.2,
      messagesInMemory: 40,
      showSources: false,
      isSuggestionsEnabled: false,
      isCarouselEnabled: false,
      isRefreshPossible: true,
      usesDurableMediaLinks: false,
      sharesMediaUrlsWithModel: false,
      showCredit: true,
      canAcceptMediaMessages: false,
      functions: [],
      ttsPrompt: '',
      promptVariables: [],
      dataToExtract: [],
      metadataFields: [],
    },
  });

  useEffect(() => {
    // Fetch available models for the dropdown
    const fetchModels = async () => {
      try {
        const response = await fetchWithAuth(`${apiUrl}/model-metadata`);
        if (response.ok) {
          const models = await response.json();
          setModelOptions(models.filter((model: ModelMetadata) => !model.isArchived));
        }
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };

    if (role === Role.ADMIN) {
      fetchModels();
    }
  }, []);

  const processChatFunctions = async (functions: ChatFunction[]) => {
    for (const f of functions) {
      if (f.id) {
        await fetchWithAuth(`${apiUrl}/chat-function/${f.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(f),
        });
      } else {
        f.chatId = id;
        const response = await fetchWithAuth(`${apiUrl}/chat-function`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(f),
        });
        if (response.status !== 200) {
          throw new Error('Something went wrong!');
        }
      }
    }
  };

  const formPropsHandler = (data: UpdateForm) => {
    data.functions?.forEach((f) => {
      f.parameters = f.parameters ? JSON.parse(f.parameters as string) : {};
    });
    data.dynamicSystemPrompts = data.dynamicSystemPrompts?.map((d) => ({
      id: Number(d.id ?? -1),
      name: d.name,
      condition: d.condition,
      prompt: d.prompt,
      zIndex: Number(d.zIndex),
    }));
    data.promptVariables = data.promptVariables?.map((v) => ({
      id: Number(v.id ?? -1),
      name: v.name,
      value: v.value,
      injectToStartOfSystemPrompt: v.injectToStartOfSystemPrompt,
      injectToEndOfSystemPrompt: v.injectToEndOfSystemPrompt,
      injectToStartOfDynamicPrompt: v.injectToStartOfDynamicPrompt,
      injectToEndOfDynamicPrompt: v.injectToEndOfDynamicPrompt,
    }));
    data.dataToExtract = data.dataToExtract?.map((f) => ({
      id: Number(f.id ?? -1),
      name: f.name,
      description: f.description,
      dataType: f.dataType,
    }));
    data.metadataFields = data.metadataFields?.map((f) => ({
      id: Number(f.id ?? -1),
      name: f.name,
      description: f.description,
      dataType: f.dataType,
    }));
    data.temperature = Number(data.temperature);
    data.messagesInMemory = Number(data.messagesInMemory);
    data.costLimit = Number(data.costLimit);
    data.showSources =
      typeof data.showSources === 'string' ? JSON.parse(data.showSources) : data.showSources;
  };

  const onSubmit: SubmitHandler<UpdateForm> = async (data) => {
    formPropsHandler(data);
    // TODO: we should use shadcn form with zod error handling
    if (!data.name || data.name.trim() === '') {
      methods.setError('name', {
        type: 'manual',
        message: t('dashboard.chatForm.validation.nameRequired'),
      });
      return;
    }
    try {
      const functions = data.functions;
      delete data.functions;
      if (id) {
        data.dynamicSystemPrompts = {
          deleteMany: { createdAt: { lt: new Date() } },
          upsert: data.dynamicSystemPrompts?.map((d: DynamicSystemPrompt) => ({
            where: { id: d.id },
            update: {
              condition: d.condition,
              prompt: d.prompt,
              name: d.name,
              zIndex: d.zIndex,
            },
            create: {
              id: undefined,
              name: d.name,
              condition: d.condition,
              prompt: d.prompt,
              zIndex: d.zIndex,
            },
          })),
        } as never;
        data.promptVariables = {
          deleteMany: { createdAt: { lt: new Date() } },
          upsert: data.promptVariables?.map((v: PromptVariable) => ({
            where: { id: v.id },
            update: {
              name: v.name,
              value: v.value,
              injectToStartOfSystemPrompt: v.injectToStartOfSystemPrompt,
              injectToEndOfSystemPrompt: v.injectToEndOfSystemPrompt,
              injectToStartOfDynamicPrompt: v.injectToStartOfDynamicPrompt,
              injectToEndOfDynamicPrompt: v.injectToEndOfDynamicPrompt,
            },
            create: {
              id: undefined,
              name: v.name,
              value: v.value,
              injectToStartOfSystemPrompt: v.injectToStartOfSystemPrompt,
              injectToEndOfSystemPrompt: v.injectToEndOfSystemPrompt,
              injectToStartOfDynamicPrompt: v.injectToStartOfDynamicPrompt,
              injectToEndOfDynamicPrompt: v.injectToEndOfDynamicPrompt,
            },
          })),
        } as never;
        data.dataToExtract = {
          deleteMany: { createdAt: { lt: new Date() } },
          upsert: data.dataToExtract?.map((f) => ({
            where: { id: f.id },
            update: {
              name: f.name,
              description: f.description,
              dataType: f.dataType,
            },
            create: {
              id: undefined,
              name: f.name,
              description: f.description,
              dataType: f.dataType,
            },
          })),
        } as never;
        data.metadataFields = {
          deleteMany: { createdAt: { lt: new Date() } },
          upsert: data.metadataFields?.map((f) => ({
            where: { id: f.id },
            update: {
              name: f.name,
              description: f.description,
              dataType: f.dataType,
            },
            create: {
              id: undefined,
              name: f.name,
              description: f.description,
              dataType: f.dataType,
            },
          })),
        } as never;
        const response = await fetchWithAuth(`${apiUrl}/chat-metadata/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (response.status !== 200) {
          throw new Error('Something went wrong!');
        }
        // NOTE: this will invalidate all chats
        queryClint.invalidateQueries({
          queryKey: [CHAT_METADATA_KEY],
        });
        refetch();
      } else {
        data.dynamicSystemPrompts = {
          create: data.dynamicSystemPrompts?.map((d: DynamicSystemPrompt) => ({
            id: undefined,
            condition: d.condition,
            prompt: d.prompt,
            zIndex: d.zIndex,
          })),
        } as never;
        data.promptVariables = {
          create: data.promptVariables?.map((v: PromptVariable) => ({
            id: undefined,
            name: v.name,
            value: v.value,
            injectToStartOfSystemPrompt: v.injectToStartOfSystemPrompt,
            injectToEndOfSystemPrompt: v.injectToEndOfSystemPrompt,
            injectToStartOfDynamicPrompt: v.injectToStartOfDynamicPrompt,
            injectToEndOfDynamicPrompt: v.injectToEndOfDynamicPrompt,
          })),
        } as never;
        data.dataToExtract = {
          create: data.dataToExtract?.map((f) => ({
            id: undefined,
            name: f.name,
            description: f.description,
            dataType: f.dataType,
          })),
        } as never;
        data.metadataFields = {
          create: data.metadataFields?.map((f) => ({
            id: undefined,
            name: f.name,
            description: f.description,
            dataType: f.dataType,
          })),
        } as never;
        const response = await fetchWithAuth(`${apiUrl}/chat-metadata`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        // TODO: we might want to check if this was successful
        const newChat = await response.json();
        refetch();
        navigate(`/${newChat.id}/edit`);
      }

      if (functions) {
        await processChatFunctions(functions);
      }
      fetchChatMetadata();
      toast.success(t('dashboard.toasts.saved'));
    } catch (error) {
      console.error('Error Submitting data:', error);
    }
  };

  const fetchChatMetadata = async () => {
    if (!id && !duplicateChatId) return; // Skip fetching if creating a new chatbot
    const chatId = duplicateChatId || id;
    try {
      const response = await fetchWithAuth(`${apiUrl}/chat-metadata/${chatId}`);
      const jsonData = await response.json();
      methods.setValue(
        'name',
        `${duplicateChatId ? t('dashboard.chats.copyOfPrefix') : ''}${jsonData.name}`,
      );
      methods.setValue('textFieldLabel', jsonData.textFieldLabel);
      methods.setValue('systemPrompt', jsonData.systemPrompt);
      methods.setValue('dynamicSystemPrompts', jsonData.dynamicSystemPrompts);
      methods.setValue('whatsappPrompt', jsonData.whatsappPrompt);
      methods.setValue('firstMessage', jsonData.firstMessage);
      methods.setValue('modelId', jsonData.modelMetadata?.id || 0);
      methods.setValue('canAcceptVoiceMessages', jsonData.canAcceptVoiceMessages);
      methods.setValue('answersModality', jsonData.answersModality);
      methods.setValue('direction', jsonData.direction);
      methods.setValue('customUserText', jsonData.customUserText);
      methods.setValue('customUserBackground', jsonData.customUserBackground);
      methods.setValue('temperature', jsonData.temperature);
      methods.setValue('showSources', jsonData.showSources);
      methods.setValue('showCredit', jsonData.showCredit);
      methods.setValue('isSuggestionsEnabled', jsonData.isSuggestionsEnabled);
      methods.setValue('isCarouselEnabled', jsonData.isCarouselEnabled);
      methods.setValue('isRefreshPossible', jsonData.isRefreshPossible);
      methods.setValue('usesDurableMediaLinks', jsonData.usesDurableMediaLinks);
      methods.setValue('sharesMediaUrlsWithModel', jsonData.sharesMediaUrlsWithModel);
      methods.setValue('canAcceptMediaMessages', jsonData.canAcceptMediaMessages);
      methods.setValue('openaiKey', jsonData.openaiKey);
      methods.setValue('googleKey', jsonData.googleKey);
      methods.setValue('twilioAccountSid', jsonData.twilioAccountSid);
      methods.setValue('twilioAuthToken', jsonData.twilioAuthToken);
      methods.setValue('twilioMessagingServiceSid', jsonData.twilioMessagingServiceSid);
      methods.setValue('assistantEmail', jsonData.assistantEmail);
      methods.setValue('sendgridApiKey', jsonData.sendgridApiKey);
      methods.setValue('airtableLogsBase', jsonData.airtableLogsBase);
      methods.setValue('messagesInMemory', jsonData.messagesInMemory);
      methods.setValue('ttsPrompt', jsonData.ttsPrompt);
      methods.setValue('promptVariables', jsonData.promptVariables);
      methods.setValue('dataToExtract', jsonData.dataToExtract);
      methods.setValue('metadataFields', jsonData.metadataFields);
      methods.setValue('costLimit', jsonData.costLimit);

      jsonData.functions?.forEach((f: ChatFunction) => {
        f.parameters = JSON.stringify(f.parameters);
      });
      methods.setValue('functions', jsonData.functions);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchChatMetadata();
  }, [id, modelOptions]);

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 px-8 pb-32"
      >
        <Tooltip>
          <div className={isAdmin ? 'col-span-1' : 'col-span-2'}>
            <div className="space-y-2">
              <InfoLabelTooltipTrigger>
                <Label variant="slate" htmlFor="name">
                  {t('dashboard.chatForm.fields.name.label')}
                </Label>
              </InfoLabelTooltipTrigger>
              <Controller
                name="name"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <Input
                      id="name"
                      {...field}
                      className="w-full"
                      placeholder={t('dashboard.chatForm.fields.name.placeholder')}
                    />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
          <InfoLabelTooltipContent>
            <p>{t('dashboard.tooltips.name')}</p>
          </InfoLabelTooltipContent>
        </Tooltip>

        {isAdmin && (
          <>
            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="modelId">
                  {t('dashboard.chatForm.fields.model.label')}
                </Label>
                <Controller
                  name="modelId"
                  control={methods.control}
                  render={({ field }) => (
                    <Select
                      value={field.value.toString()}
                      onValueChange={(value) => field.onChange(Number(value))}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t('dashboard.chatForm.fields.model.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {modelOptions.map((model) => (
                          <SelectItem key={model.id} value={model.id.toString()}>
                            {model.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label variant="slate" htmlFor="costLimit">
                {t('dashboard.chatForm.fields.costLimit.label')}
              </Label>
              <Controller
                name="costLimit"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <Input
                      id="costLimit"
                      {...field}
                      className="w-full"
                      placeholder={t('dashboard.chatForm.fields.costLimit.placeholder')}
                      type="number"
                      onChange={(e) => {
                        const value = e.target.value;
                        const intValue = parseInt(value, 10);
                        field.onChange(isNaN(intValue) ? '' : intValue);
                      }}
                    />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="answersModality">
                  {t('dashboard.chatForm.fields.answersModality.label')}
                </Label>
                <Controller
                  name="answersModality"
                  control={methods.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t('dashboard.chatForm.fields.answersModality.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AnswersModality.TEXT}>
                          {t('dashboard.chatForm.fields.answersModality.options.text')}
                        </SelectItem>
                        <SelectItem value={AnswersModality.VOICE}>
                          {t('dashboard.chatForm.fields.answersModality.options.voice')}
                        </SelectItem>
                        <SelectItem value={AnswersModality.TEXT_AND_VOICE}>
                          {t('dashboard.chatForm.fields.answersModality.options.textAndVoice')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="showCredit"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="showCredit"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="showCredit">
                      {t('dashboard.chatForm.fields.showCredit')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="isSuggestionsEnabled"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isSuggestionsEnabled"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="isSuggestionsEnabled">
                      {t('dashboard.chatForm.fields.isSuggestionsEnabled')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="isCarouselEnabled"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isCarouselEnabled"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="isCarouselEnabled">
                      {t('dashboard.chatForm.fields.isCarouselEnabled')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="isRefreshPossible"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isRefreshPossible"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="isRefreshPossible">
                      {t('dashboard.chatForm.fields.isRefreshPossible')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="usesDurableMediaLinks"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="usesDurableMediaLinks"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="usesDurableMediaLinks">
                      {t('dashboard.chatForm.fields.usesDurableMediaLinks')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="sharesMediaUrlsWithModel"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sharesMediaUrlsWithModel"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="sharesMediaUrlsWithModel">
                      {t('dashboard.chatForm.fields.sharesMediaUrlsWithModel')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="canAcceptMediaMessages"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="acceptMedia"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="acceptMedia">
                      {t('dashboard.chatForm.fields.canAcceptMediaMessages')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-1 flex items-center">
              <Controller
                name="canAcceptVoiceMessages"
                control={methods.control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="acceptVoice"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <Label variant="slate" htmlFor="acceptVoice">
                      {t('dashboard.chatForm.fields.canAcceptVoiceMessages')}
                    </Label>
                  </div>
                )}
              />
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="ttsPrompt">
                  {t('dashboard.chatForm.fields.ttsPrompt.label')}
                </Label>
                <Controller
                  name="ttsPrompt"
                  control={methods.control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={t('dashboard.chatForm.fields.ttsPrompt.placeholder')}
                      rows={4}
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}

        <Tooltip>
          <div className="col-span-2">
            <div className="space-y-2">
              <InfoLabelTooltipTrigger>
                <Label variant="slate" htmlFor="systemPrompt">
                  {t('dashboard.chatForm.fields.systemPrompt.label')}
                </Label>
              </InfoLabelTooltipTrigger>
              <Controller
                name="systemPrompt"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <textarea
                      {...field}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={t('dashboard.chatForm.fields.systemPrompt.placeholder')}
                      rows={20}
                    />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
          <InfoLabelTooltipContent>
            <p>{t('dashboard.tooltips.systemPrompt.p0')}</p>
            <p>{t('dashboard.tooltips.systemPrompt.p1')}</p>
            <p>{t('dashboard.tooltips.systemPrompt.p2')}</p>
          </InfoLabelTooltipContent>
        </Tooltip>

        {canEditDynamicPrompts && (
          <div className="col-span-2 flex justify-center -mt-4">
            <Button variant="link" onClick={() => setIsDynamicPromptDialogOpen(true)}>
              {t('dashboard.chatForm.fields.dynamicPromptsButton')}
            </Button>
          </div>
        )}

        {isAdmin && (
          <div className="col-span-2">
            <div className="space-y-2">
              <Label variant="slate" htmlFor="whatsappPrompt">
                {t('dashboard.chatForm.fields.whatsappPrompt.label')}
              </Label>
              <Controller
                name="whatsappPrompt"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <textarea
                      {...field}
                      className="flex min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={t('dashboard.chatForm.fields.whatsappPrompt.placeholder')}
                      rows={6}
                    />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
        )}

        <Tooltip>
          <div className="col-span-2">
            <div className="space-y-2">
              <InfoLabelTooltipTrigger>
                <Label variant="slate" htmlFor="firstMessage">
                  {t('dashboard.chatForm.fields.openingMessage.label')}
                </Label>
              </InfoLabelTooltipTrigger>

              <Controller
                name="firstMessage"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <textarea
                      {...field}
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={t('dashboard.chatForm.fields.openingMessage.placeholder')}
                      rows={8}
                    />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
          <InfoLabelTooltipContent>
            <p>{t('dashboard.tooltips.openingMessage.p0')}</p>
            <p>{t('dashboard.tooltips.openingMessage.p1')}</p>
          </InfoLabelTooltipContent>
        </Tooltip>

        {isAdmin && (
          <div className="col-span-2">
            <div className="space-y-2">
              <Label variant="slate" htmlFor="textFieldLabel">
                {t('dashboard.chatForm.fields.textFieldLabel')}
              </Label>
              <Controller
                name="textFieldLabel"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="space-y-1">
                    <Input id="textFieldLabel" {...field} className="w-full" />
                    {error && <p className="text-sm text-destructive">{error.message}</p>}
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {isAdmin && (
          <>
            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="customUserText">
                  {t('dashboard.chatForm.fields.messageTextColor')}
                </Label>
                <Controller
                  name="customUserText"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="customUserText" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="customUserBackground">
                  {t('dashboard.chatForm.fields.messageBackgroundColor')}
                </Label>
                <Controller
                  name="customUserBackground"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="customUserBackground" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="temperature">
                  {t('dashboard.chatForm.fields.temperature')}
                </Label>
                <Controller
                  name="temperature"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="temperature" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="showSources">
                  {t('dashboard.chatForm.fields.showSources.label')}
                </Label>
                <Controller
                  name="showSources"
                  control={methods.control}
                  render={({ field }) => (
                    <Select
                      value={field.value.toString()}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t('dashboard.chatForm.fields.showSources.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">
                          {t('dashboard.chatForm.fields.showSources.options.true')}
                        </SelectItem>
                        <SelectItem value="false">
                          {t('dashboard.chatForm.fields.showSources.options.false')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1 md:col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="messagesInMemory">
                  {t('dashboard.chatForm.fields.messagesInMemory')}
                </Label>
                <Controller
                  name="messagesInMemory"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="messagesInMemory" {...field} className="w-full" type="number" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="direction">
                  {t('dashboard.chatForm.fields.direction.label')}
                </Label>
                <Controller
                  name="direction"
                  control={methods.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t('dashboard.chatForm.fields.direction.placeholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(Direction).map((dir) => (
                          <SelectItem key={dir} value={dir}>
                            {dir}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            {/* API Keys */}
            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="openaiKey">
                  {t('dashboard.chatForm.fields.openaiApiKey')}
                </Label>
                <Controller
                  name="openaiKey"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="openaiKey" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>
            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="googleKey">
                  {t('dashboard.chatForm.fields.googleApiKey')}
                </Label>
                <Controller
                  name="googleKey"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="googleKey" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="twilioAccountSid">
                  {t('dashboard.chatForm.fields.twilioAccountSid')}
                </Label>
                <Controller
                  name="twilioAccountSid"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="twilioAccountSid" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="twilioAuthToken">
                  {t('dashboard.chatForm.fields.twilioAuthToken')}
                </Label>
                <Controller
                  name="twilioAuthToken"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="twilioAuthToken" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="twilioMessagingServiceSid">
                  {t('dashboard.chatForm.fields.twilioMessagingServiceSid')}
                </Label>
                <Controller
                  name="twilioMessagingServiceSid"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="twilioMessagingServiceSid" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="sendgridApiKey">
                  {t('dashboard.chatForm.fields.sendgridApiKey')}
                </Label>
                <Controller
                  name="sendgridApiKey"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="sendgridApiKey" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-1">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="assistantEmail">
                  {t('dashboard.chatForm.fields.assistantEmail')}
                </Label>
                <Controller
                  name="assistantEmail"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input id="assistantEmail" {...field} className="w-full" />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <div className="space-y-2">
                <Label variant="slate" htmlFor="airtableLogsBase" className="flex items-center">
                  <span className="me-2">📊</span> Airtable Logs Base
                </Label>
                <Controller
                  name="airtableLogsBase"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <Input
                        id="airtableLogsBase"
                        {...field}
                        className="w-full transition-[border-color,box-shadow] hover:border-blue-300 focus:ring-2 focus:ring-blue-200"
                      />
                      {error && <p className="text-sm text-destructive">{error.message}</p>}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="col-span-2">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                <h2 className="text-indigo-700 text-xl font-medium mb-3 flex items-center text-balance">
                  <span className="bg-indigo-100 p-1.5 rounded-lg me-2">🤖</span>
                  {t('dashboard.chatForm.sections.functions.title')}
                  <span className="text-sm text-indigo-500 ms-2 font-normal">
                    {t('dashboard.chatForm.sections.functions.subtitle')}
                  </span>
                </h2>
                <FunctionRepeater />
              </div>
            </div>

            <div className="col-span-2">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-100 shadow-sm mt-3">
                <h2 className="text-emerald-700 text-xl font-medium mb-3 flex items-center text-balance">
                  <span className="bg-emerald-100 p-1.5 rounded-lg me-2">📝</span>
                  {t('dashboard.chatForm.sections.dataToExtract.title')}
                  <span className="text-sm text-emerald-500 ms-2 font-normal">
                    {t('dashboard.chatForm.sections.dataToExtract.subtitle')}
                  </span>
                </h2>
                <ExtractedDataFieldRepeater />
              </div>
            </div>

            <div className="col-span-2">
              <div className="bg-linear-to-r from-sky-50 to-cyan-50 p-4 rounded-lg border border-sky-100 shadow-sm mt-3">
                <h2 className="text-sky-700 text-xl font-medium mb-3 flex items-center text-balance">
                  <span className="bg-sky-100 p-1.5 rounded-lg me-2">🏷️</span>
                  {t('dashboard.chatForm.sections.metadataFields.title')}
                  <span className="text-sm text-sky-500 ms-2 font-normal">
                    {t('dashboard.chatForm.sections.metadataFields.subtitle')}
                  </span>
                </h2>
                <MetadataFieldRepeater />
              </div>
            </div>
          </>
        )}
      </form>
      <Dialog open={isDynamicPromptDialogOpen} onOpenChange={setIsDynamicPromptDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dashboard.chatForm.dialogs.dynamicSystemPromptsTitle')}</DialogTitle>
          </DialogHeader>
          <DynamicSystemPromptRepeater />
          <DialogClose />
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
});

UpdateChatForm.displayName = 'UpdateChatForm';
export { UpdateChatForm };
